import moment from 'moment';

const hasField = (registrant, keywords) => {
  if (!registrant) return false;
  
  // 1. Check in schema snapshot if available
  if (registrant.responseSchemaSnapshot) {
    for (const field of registrant.responseSchemaSnapshot) {
      if (field.type === 'label') continue;
      const labelLower = (field.label || '').toLowerCase();
      const fieldKeyLower = (field.fieldKey || '').toLowerCase();
      for (const kw of keywords) {
        if (labelLower.includes(kw.toLowerCase()) || fieldKeyLower.includes(kw.toLowerCase())) {
          return true;
        }
      }
    }
  }

  // 2. Check direct key match in responses if snapshot is not available or empty
  if (registrant.responses) {
    const keys = Object.keys(registrant.responses);
    for (const key of keys) {
      const keyLower = key.toLowerCase();
      for (const kw of keywords) {
        if (keyLower === kw.toLowerCase() || keyLower.replace(/[^a-z0-9]/g, '') === kw.toLowerCase().replace(/[^a-z0-9]/g, '')) {
          return true;
        }
      }
    }
  }

  return false;
};

const findValue = (registrant, keywords, standardValue = '') => {
  if (!registrant || !registrant.responses) return standardValue || '';

  const responses = registrant.responses;
  // Look for direct key match
  for (const key of Object.keys(responses)) {
    const keyLower = key.toLowerCase();
    for (const kw of keywords) {
      if (keyLower === kw.toLowerCase() || keyLower.replace(/[^a-z0-9]/g, '') === kw.toLowerCase().replace(/[^a-z0-9]/g, '')) {
        return responses[key];
      }
    }
  }

  // Look for label or key in schema snapshot
  if (registrant.responseSchemaSnapshot) {
    for (const field of registrant.responseSchemaSnapshot) {
      const labelLower = (field.label || '').toLowerCase();
      const fieldKeyLower = (field.fieldKey || '').toLowerCase();
      for (const kw of keywords) {
        if (labelLower.includes(kw.toLowerCase()) || fieldKeyLower.includes(kw.toLowerCase())) {
          const val = responses[field.fieldKey] !== undefined ? responses[field.fieldKey] : responses[field.fieldKey.toLowerCase()];
          if (val !== undefined && val !== null) return val;
        }
      }
    }
  }
  return standardValue || '';
};

export const getEventPrintTemplate = (eventDetail, registrant = null) => {
  // Resolve standard/custom values
  let fullName = '';
  if (registrant) {
    if (registrant.personDetail?.name) {
      fullName = registrant.personDetail.name;
    } else {
      const firstName = registrant.responses?.firstName || registrant.responses?.firstname || '';
      const lastName = registrant.responses?.lastName || registrant.responses?.lastname || '';
      fullName = `${firstName} ${lastName}`.trim();
    }
  }

  // Check presence of custom fields
  const showOccupation = hasField(registrant, ['occupation', 'job', 'व्यवसाय', 'જોબ', 'વ્યવસાય', 'ધંધો']);
  const showDob = hasField(registrant, ['dob', 'birth', 'birthdate', 'જન્મતારીખ', 'જન્મ તારીખ']);
  const showVillage = hasField(registrant, ['village', 'town', 'ગામ', 'વતન']);
  const showWhatsapp = hasField(registrant, ['whatsapp', 'whats app', 'વો.એપ', 'વોએપ', 'વોટ્સએપ']);
  const showLicense = hasField(registrant, ['license', 'driving license', 'લાયસન્સ', 'લાઈસન્સ', 'લાઇસન્સ']);
  const showVehicle = hasField(registrant, ['vehicle', 'car', 'bike', 'ગાડી', 'વાહન', 'ગાડી નં']);
  const showFatherMobile = hasField(registrant, ['father mobile', 'father_mobile', 'fathermobile', 'પિતાનો મો', 'પિતા નો']);
  const showAdmissionDate = hasField(registrant, ['admission date', 'admission_date', 'entry date', 'entry_date', 'પ્રવેશ તારીખ', 'પ્રવેશ તા', 'પ્રવેશ']);

  // Recommenders
  const showRef1 = hasField(registrant, ['ref1', 'reference1', 'reference 1', 'રેફરેન્સ-૧', 'રેફરેન્સ ૧', 'રેફરન્સ ૧', 'રેફરન્સ 1']);
  const showRef2 = hasField(registrant, ['ref2', 'reference2', 'reference 2', 'રેફરેન્સ-૨', 'રેફરેન્સ ૨', 'રેફરન્સ ૨', 'રેફરન્સ 2']);

  // Extract values
  const occupation = showOccupation ? findValue(registrant, ['occupation', 'job', 'व्यवसाय', 'જોબ', 'વ્યવસાય', 'ધંધો']) : '';
  
  let dob = '';
  if (showDob && registrant) {
    const rawDob = registrant.personDetail?.dob || findValue(registrant, ['dob', 'birth', 'birthdate', 'જન્મતારીખ', 'જન્મ તારીખ']);
    if (rawDob) {
      dob = moment(rawDob).isValid() ? moment(rawDob).format('DD-MM-YYYY') : rawDob;
    }
  }

  const village = showVillage ? findValue(registrant, ['village', 'town', 'ગામ', 'વતન'], registrant?.personDetail?.village || registrant?.personDetail?.city) : '';
  const mobile = registrant?.personDetail?.mobile || findValue(registrant, ['mobile', 'phone', 'contact', 'મોબાઇલ', 'મોબાઈલ']);
  const whatsapp = showWhatsapp ? findValue(registrant, ['whatsapp', 'whats app', 'વો.એપ', 'વોએપ', 'વોટ્સએપ']) : '';
  const license = showLicense ? findValue(registrant, ['license', 'driving license', 'લાયસન્સ', 'લાઈસન્સ', 'લાઇસન્સ']) : '';
  const vehicle = showVehicle ? findValue(registrant, ['vehicle', 'car', 'bike', 'ગાડી', 'વાહન', 'ગાડી નં']) : '';
  const fatherMobile = showFatherMobile ? findValue(registrant, ['father mobile', 'father_mobile', 'fathermobile', 'પિતાનો મો', 'પિતા નો']) : '';

  let admissionDate = '';
  if (showAdmissionDate && registrant) {
    const rawAdmissionDate = findValue(registrant, ['admission date', 'admission_date', 'entry date', 'entry_date', 'પ્રવેશ તારીખ', 'પ્રવેશ તા', 'પ્રવેશ']);
    if (rawAdmissionDate) {
      admissionDate = moment(rawAdmissionDate).isValid() ? moment(rawAdmissionDate).format('DD-MM-YYYY') : rawAdmissionDate;
    }
  }

  const ref1Name = showRef1 ? findValue(registrant, ['ref1_name', 'ref1name', 'reference1_name', 'reference 1 name', 'રેફરેન્સ-૧ નામ', 'રેફરેન્સ ૧ નામ', 'રેફરન્સ ૧', 'રેફરન્સ 1', 'રેફરન્સ 1 નામ', 'રેફરેન્સ 1 નામ']) : '';
  const ref1Mobile = showRef1 ? findValue(registrant, ['ref1_mob', 'ref1mobile', 'ref1_phone', 'reference1_mobile', 'reference 1 mobile', 'રેફરેન્સ-૧ મો', 'રેફરેન્સ ૧ મો', 'રેફરન્સ ૧ મો', 'રેફરન્સ 1 મો', 'રેફરન્સ 1 મોબાઇલ', 'રેફરન્સ 1 મોબાઇલ નંબર']) : '';
  const ref2Name = showRef2 ? findValue(registrant, ['ref2_name', 'ref2name', 'reference2_name', 'reference 2 name', 'રેફરેન્સ-૨ નામ', 'રેફરેન્સ ૨ નામ', 'રેફરન્સ ૨', 'રેફરન્સ 2', 'રેફરન્સ 2 નામ', 'રેફરેન્સ 2 નામ']) : '';
  const ref2Mobile = showRef2 ? findValue(registrant, ['ref2_mob', 'ref2mobile', 'ref2_phone', 'reference2_mobile', 'reference 2 mobile', 'રેફરેન્સ-૨ મો', 'રેફરેન્સ ૨ મો', 'રેફરન્સ ૨ મો', 'રેફરન્સ 2 મો', 'રેફરન્સ 2 મોબાઇલ', 'રેફરન્સ 2 મોબાઇલ નંબર']) : '';

  // Dynamic layout fields construction
  let fieldsHTML = '';

  // 1. Full Name (Always present)
  fieldsHTML += `
    <div style="display: flex; margin-bottom: 8px; align-items: baseline;">
      <span style="font-weight: bold; width: 120px; min-width: 120px;">પૂરૂ નામ</span>
      <span style="margin-right: 10px;">:</span>
      <span style="flex-grow: 1; border-bottom: 1px dotted #000; padding-bottom: 2px; font-weight: 600; font-size: 16px;">
        ${fullName || '&nbsp;'}
      </span>
    </div>
  `;

  // 2. Occupation (If configured)
  if (showOccupation) {
    fieldsHTML += `
      <div style="display: flex; margin-bottom: 8px; align-items: baseline;">
        <span style="font-weight: bold; width: 120px; min-width: 120px;">વ્યવસાય/જોબ</span>
        <span style="margin-right: 10px;">:</span>
        <span style="flex-grow: 1; border-bottom: 1px dotted #000; padding-bottom: 2px; font-weight: 500;">
          ${occupation || '&nbsp;'}
        </span>
      </div>
    `;
  }

  // 3. Birth Date & Village (Conditional row)
  if (showDob && showVillage) {
    fieldsHTML += `
      <div style="display: flex; margin-bottom: 8px; align-items: baseline; width: 100%;">
        <div style="display: flex; flex: 1.2; align-items: baseline; margin-right: 20px;">
          <span style="font-weight: bold; width: 120px; min-width: 120px;">જન્મતારીખ</span>
          <span style="margin-right: 10px;">:</span>
          <span style="flex-grow: 1; border-bottom: 1px dotted #000; padding-bottom: 2px; font-weight: 500;">
            ${dob || '&nbsp;'}
          </span>
        </div>
        <div style="display: flex; flex: 1; align-items: baseline;">
          <span style="font-weight: bold; width: 80px; min-width: 80px;">ગામ</span>
          <span style="margin-right: 10px;">:</span>
          <span style="flex-grow: 1; border-bottom: 1px dotted #000; padding-bottom: 2px; font-weight: 500;">
            ${village || '&nbsp;'}
          </span>
        </div>
      </div>
    `;
  } else if (showDob) {
    fieldsHTML += `
      <div style="display: flex; margin-bottom: 8px; align-items: baseline; width: 100%;">
        <span style="font-weight: bold; width: 120px; min-width: 120px;">જન્મતારીખ</span>
        <span style="margin-right: 10px;">:</span>
        <span style="flex-grow: 1; border-bottom: 1px dotted #000; padding-bottom: 2px; font-weight: 500;">
          ${dob || '&nbsp;'}
        </span>
      </div>
    `;
  } else if (showVillage) {
    fieldsHTML += `
      <div style="display: flex; margin-bottom: 8px; align-items: baseline; width: 100%;">
        <span style="font-weight: bold; width: 120px; min-width: 120px;">ગામ</span>
        <span style="margin-right: 10px;">:</span>
        <span style="flex-grow: 1; border-bottom: 1px dotted #000; padding-bottom: 2px; font-weight: 500;">
          ${village || '&nbsp;'}
        </span>
      </div>
    `;
  }

  // 4. Mobile & WhatsApp (WhatsApp is conditional)
  if (showWhatsapp) {
    fieldsHTML += `
      <div style="display: flex; margin-bottom: 8px; align-items: baseline; width: 100%;">
        <div style="display: flex; flex: 1.2; align-items: baseline; margin-right: 20px;">
          <span style="font-weight: bold; width: 120px; min-width: 120px;">મોબાઇલ નં.</span>
          <span style="margin-right: 10px;">:</span>
          <span style="flex-grow: 1; border-bottom: 1px dotted #000; padding-bottom: 2px; font-weight: 500;">
            ${mobile || '&nbsp;'}
          </span>
        </div>
        <div style="display: flex; flex: 1; align-items: baseline;">
          <span style="font-weight: bold; width: 80px; min-width: 80px;">વો.એપ નં.</span>
          <span style="margin-right: 10px;">:</span>
          <span style="flex-grow: 1; border-bottom: 1px dotted #000; padding-bottom: 2px; font-weight: 500;">
            ${whatsapp || '&nbsp;'}
          </span>
        </div>
      </div>
    `;
  } else {
    fieldsHTML += `
      <div style="display: flex; margin-bottom: 8px; align-items: baseline; width: 100%;">
        <span style="font-weight: bold; width: 120px; min-width: 120px;">મોબાઇલ નં.</span>
        <span style="margin-right: 10px;">:</span>
        <span style="flex-grow: 1; border-bottom: 1px dotted #000; padding-bottom: 2px; font-weight: 500;">
          ${mobile || '&nbsp;'}
        </span>
      </div>
    `;
  }

  // 5. Gender & City (Always present standard fields)
  const genderLabel = registrant?.personDetail?.gender?.toLowerCase() === 'male' ? 'પુરુષ (Male)' : 'સ્ત્રી (Female)';
  fieldsHTML += `
    <div style="display: flex; margin-bottom: 8px; align-items: baseline; width: 100%;">
      <div style="display: flex; flex: 1.2; align-items: baseline; margin-right: 20px;">
        <span style="font-weight: bold; width: 120px; min-width: 120px;">જાતિ (Gender)</span>
        <span style="margin-right: 10px;">:</span>
        <span style="flex-grow: 1; border-bottom: 1px dotted #000; padding-bottom: 2px; font-weight: 500;">
          ${genderLabel}
        </span>
      </div>
      <div style="display: flex; flex: 1; align-items: baseline;">
        <span style="font-weight: bold; width: 80px; min-width: 80px;">શહેર</span>
        <span style="margin-right: 10px;">:</span>
        <span style="flex-grow: 1; border-bottom: 1px dotted #000; padding-bottom: 2px; font-weight: 500;">
          ${registrant?.personDetail?.city || '&nbsp;'}
        </span>
      </div>
    </div>
  `;

  // 6. License & Vehicle (Conditional row)
  if (showLicense && showVehicle) {
    fieldsHTML += `
      <div style="display: flex; margin-bottom: 8px; align-items: baseline; width: 100%;">
        <div style="display: flex; flex: 1.2; align-items: baseline; margin-right: 20px;">
          <span style="font-weight: bold; width: 120px; min-width: 120px;">લાયસન્સ નં.</span>
          <span style="margin-right: 10px;">:</span>
          <span style="flex-grow: 1; border-bottom: 1px dotted #000; padding-bottom: 2px; font-weight: 500;">
            ${license || '&nbsp;'}
          </span>
        </div>
        <div style="display: flex; flex: 1; align-items: baseline;">
          <span style="font-weight: bold; width: 80px; min-width: 80px;">ગાડી નં.</span>
          <span style="margin-right: 10px;">:</span>
          <span style="flex-grow: 1; border-bottom: 1px dotted #000; padding-bottom: 2px; font-weight: 500;">
            ${vehicle || '&nbsp;'}
          </span>
        </div>
      </div>
    `;
  } else if (showLicense) {
    fieldsHTML += `
      <div style="display: flex; margin-bottom: 8px; align-items: baseline; width: 100%;">
        <span style="font-weight: bold; width: 120px; min-width: 120px;">લાયસન્સ નં.</span>
        <span style="margin-right: 10px;">:</span>
        <span style="flex-grow: 1; border-bottom: 1px dotted #000; padding-bottom: 2px; font-weight: 500;">
          ${license || '&nbsp;'}
        </span>
      </div>
    `;
  } else if (showVehicle) {
    fieldsHTML += `
      <div style="display: flex; margin-bottom: 8px; align-items: baseline; width: 100%;">
        <span style="font-weight: bold; width: 120px; min-width: 120px;">ગાડી નં.</span>
        <span style="margin-right: 10px;">:</span>
        <span style="flex-grow: 1; border-bottom: 1px dotted #000; padding-bottom: 2px; font-weight: 500;">
          ${vehicle || '&nbsp;'}
        </span>
      </div>
    `;
  }

  // 7. Father's Mobile & Admission Date (Conditional row)
  if (showFatherMobile && showAdmissionDate) {
    fieldsHTML += `
      <div style="display: flex; margin-bottom: 8px; align-items: baseline; width: 100%;">
        <div style="display: flex; flex: 1.2; align-items: baseline; margin-right: 20px;">
          <span style="font-weight: bold; width: 120px; min-width: 120px;">પિતાનો મો. નં.</span>
          <span style="margin-right: 10px;">:</span>
          <span style="flex-grow: 1; border-bottom: 1px dotted #000; padding-bottom: 2px; font-weight: 500;">
            ${fatherMobile || '&nbsp;'}
          </span>
        </div>
        <div style="display: flex; flex: 1; align-items: baseline;">
          <span style="font-weight: bold; width: 90px; min-width: 90px;">પ્રવેશ તારીખ</span>
          <span style="margin-right: 10px;">:</span>
          <span style="flex-grow: 1; border-bottom: 1px dotted #000; padding-bottom: 2px; font-weight: 500;">
            ${admissionDate || '&nbsp;'}
          </span>
        </div>
      </div>
    `;
  } else if (showFatherMobile) {
    fieldsHTML += `
      <div style="display: flex; margin-bottom: 8px; align-items: baseline; width: 100%;">
        <span style="font-weight: bold; width: 120px; min-width: 120px;">પિતાનો મો. નં.</span>
        <span style="margin-right: 10px;">:</span>
        <span style="flex-grow: 1; border-bottom: 1px dotted #000; padding-bottom: 2px; font-weight: 500;">
          ${fatherMobile || '&nbsp;'}
        </span>
      </div>
    `;
  } else if (showAdmissionDate) {
    fieldsHTML += `
      <div style="display: flex; margin-bottom: 8px; align-items: baseline; width: 100%;">
        <span style="font-weight: bold; width: 120px; min-width: 120px;">પ્રવેશ તારીખ</span>
        <span style="margin-right: 10px;">:</span>
        <span style="flex-grow: 1; border-bottom: 1px dotted #000; padding-bottom: 2px; font-weight: 500;">
          ${admissionDate || '&nbsp;'}
        </span>
      </div>
    `;
  }

  // 8. Address (Always present standard field)
  fieldsHTML += `
    <div style="display: flex; margin-bottom: 8px; align-items: baseline; width: 100%;">
      <span style="font-weight: bold; width: 120px; min-width: 120px;">સરનામું</span>
      <span style="margin-right: 10px;">:</span>
      <span style="flex-grow: 1; border-bottom: 1px dotted #000; padding-bottom: 2px; font-weight: 500; font-size: 14px;">
        ${registrant?.personDetail?.address || '&nbsp;'}
      </span>
    </div>
  `;

  // Recommenders block
  let recommendersHTML = '';
  if (showRef1 || showRef2) {
    recommendersHTML = `
      <div style="text-align: center; margin-top: 20px; margin-bottom: 15px;">
        <h3 style="margin: 0 auto; font-size: 16px; font-weight: bold; color: #000;">
          : યુવાનની ભલામણ કરનાર ની વિગત :
        </h3>
      </div>

      <div style="margin-bottom: 8px; font-size: 14px;">
        ${showRef1 ? `
          <div style="display: flex; margin-bottom: 8px; align-items: baseline; width: 100%;">
            <div style="display: flex; flex: 2; align-items: baseline; margin-right: 20px;">
              <span style="font-weight: bold; width: 120px; min-width: 120px;">રેફરેન્સ-૧ નામ</span>
              <span style="margin-right: 10px;">:</span>
              <span style="flex-grow: 1; border-bottom: 1px dotted #000; padding-bottom: 2px; font-weight: 500;">
                ${ref1Name || '&nbsp;'}
              </span>
            </div>
            <div style="display: flex; flex: 1; align-items: baseline;">
              <span style="font-weight: bold; width: 80px; min-width: 80px;">મો.નં.</span>
              <span style="margin-right: 10px;">:</span>
              <span style="flex-grow: 1; border-bottom: 1px dotted #000; padding-bottom: 2px; font-weight: 500;">
                ${ref1Mobile || '&nbsp;'}
              </span>
            </div>
          </div>
        ` : ''}

        ${showRef2 ? `
          <div style="display: flex; margin-bottom: 8px; align-items: baseline; width: 100%;">
            <div style="display: flex; flex: 2; align-items: baseline; margin-right: 20px;">
              <span style="font-weight: bold; width: 120px; min-width: 120px;">રેફરેન્સ-૨ નામ</span>
              <span style="margin-right: 10px;">:</span>
              <span style="flex-grow: 1; border-bottom: 1px dotted #000; padding-bottom: 2px; font-weight: 500;">
                ${ref2Name || '&nbsp;'}
              </span>
            </div>
            <div style="display: flex; flex: 1; align-items: baseline;">
              <span style="font-weight: bold; width: 80px; min-width: 80px;">મો.નં.</span>
              <span style="margin-right: 10px;">:</span>
              <span style="flex-grow: 1; border-bottom: 1px dotted #000; padding-bottom: 2px; font-weight: 500;">
                ${ref2Mobile || '&nbsp;'}
              </span>
            </div>
          </div>
        ` : ''}
      </div>
    `;
  }

  // Return full HTML string
  return `
    <style>
      @media print {
        @page {
          size: A4;
          margin: 8mm 12mm !important;
        }
        html, body {
          height: 100% !important;
          overflow: hidden !important;
          box-sizing: border-box;
        }
      }
    </style>
    <div style="font-family: 'Outfit', 'Inter', 'Helvetica Neue', Arial, sans-serif; padding: 5px 25px 0px 25px; color: #000; line-height: 1.4; max-width: 800px; margin: 0 auto; background-color: #fff; box-sizing: border-box; page-break-inside: avoid; break-inside: avoid;">
      
      <!-- Header Section -->
      <div style="text-align: center; margin-bottom: 10px; position: relative;">
        <h2 style="margin: 0; font-size: 24px; font-weight: bold; color: #111;">
          ${eventDetail?.eventName || 'Event Registration'}
        </h2>
        <div style="margin-top: 3px; font-size: 14px; color: #444;">
          ${eventDetail?.eventDate ? `<span style="margin-right: 15px;"><b>તારીખ / સમય:</b> ${eventDetail.eventDate}</span>` : ''}
          ${eventDetail?.location ? `<span><b>સ્થળ:</b> ${eventDetail.location}</span>` : ''}
        </div>
        <div style="border-bottom: 1.5px solid #000; margin-top: 5px; margin-bottom: 5px;"></div>
        <h3 style="margin: 16px auto 0 auto; font-size: 16px; font-weight: bold; color: #000; letter-spacing: 0.5px;">
          મારી વિગત નીચે મુજબ છે.
        </h3>
      </div>

      <!-- Main Fields Section -->
      <div style="margin-bottom: 8px; font-size: 14px;">
        ${fieldsHTML}
      </div>

      <!-- Recommenders Details Section -->
      ${recommendersHTML}

      <!-- Special Notes Section -->
      <div style="margin-top: 20px; font-size: 14px; line-height: 1.5;">
        <div style="text-align: center; margin-bottom: 6px;">
          <h4 style="margin: 0; font-size: 15px; font-weight: bold;">: ખાસ નોંધ :</h4>
        </div>
        <p style="font-weight: bold; margin-bottom: 6px; text-align: justify; text-indent: 20px; border: 1.5px solid #000; padding: 5px 8px; border-radius: 4px; line-height: 1.4;">
          પોતાના, પિતાના તથા બંને ભલામણ કરનારના આધાર કાર્ડની કોપી તથા પોતાના ૨ ફોટા ફરજીયાત ઓફિસમાં કોઠારીશ્રી ને જમા કરાવવા.
        </p>
        <div style="font-weight: 500; color: #222; font-size: 13px; line-height: 1.4;">
          <p style="margin: 0 0 3px 0;">• ઉપરોક્ત નિયમોનો ભંગ કરનારને અહીંથી રજા આપી દેવામાં આવશે.</p>
          <p style="margin: 0 0 3px 0;">• કોઇ પણ પ્રકારની દલીલ સાંભળવામાં આવશે નહિ.</p>
          <p style="margin: 0 0 5px 0;">• આખરી નિર્ણય કોઠારીશ્રી નો રહેશે.</p>
        </div>
        
        <div style="display: flex; justify-content: space-between; margin-top: 20px; align-items: flex-end;">
          <div style="font-size: 14px;">
            <b>પોતાની સહિ :</b> ___________________________
          </div>
          <div style="text-align: right; font-size: 14px; font-weight: bold;">
            લિ. કોઠારીશ્રી.
          </div>
        </div>
      </div>

    </div>
  `;
};
